import { ReactNode, useMemo } from 'react';
import { AlertCircle, History, Loader2 } from 'lucide-react';
import { normalizeLegacyLineBreaks, parseLegacyLogEntries } from '@shared/types';
import { LinkText } from '@/components/LinkText';
import { Badge } from '@/components/ui/badge';

export interface MigratedAuditLog {
  id: string;
  action: 'CREATE' | 'UPDATE' | 'DELETE';
  modelName: string;
  recordId: string;
  userId: string | null;
  userName: string | null;
  dataBefore: Record<string, unknown> | null;
  dataAfter: Record<string, unknown> | null;
  diff: Record<string, { old: unknown; new: unknown }> | null;
  ip: string | null;
  userAgent: string | null;
  createdAt: string;
}

const DEFAULT_HIDDEN_AUDIT_FIELDS = new Set(['id', 'createdAt', 'updatedAt']);

export function formatMigratedDateTime(value?: string | null): string {
  if (!value) return '';
  return new Date(value).toLocaleString('ru-RU', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

function auditActionLabel(action: MigratedAuditLog['action']): string {
  if (action === 'CREATE') return 'Создание';
  if (action === 'DELETE') return 'Удаление';
  return 'Изменение';
}

function auditActionClass(action: MigratedAuditLog['action']): string {
  if (action === 'CREATE') return 'bg-green-50 text-green-700 border-green-200';
  if (action === 'DELETE') return 'bg-red-50 text-red-700 border-red-200';
  return 'bg-blue-50 text-blue-700 border-blue-200';
}

function formatAuditValue(value: unknown, fieldName?: string): string {
  if (value === null || value === undefined || value === '') return '—';
  if (value === true) return 'Да';
  if (value === false) return 'Нет';

  if (typeof value === 'string') {
    const normalized = normalizeLegacyLineBreaks(value);
    if (/^\d{4}-\d{2}-\d{2}T/.test(normalized) || fieldName?.endsWith('At')) {
      return formatMigratedDateTime(normalized);
    }
    return normalized;
  }

  if (typeof value === 'number') return value.toLocaleString('ru-RU');
  if (typeof value === 'object') return JSON.stringify(value, null, 2);

  return String(value);
}

function auditSortTime(item: MigratedAuditLog): number {
  const parsed = Date.parse(item.createdAt);
  return Number.isNaN(parsed) ? 0 : parsed;
}

function AuditEntry({
  item,
  fieldLabels,
  hiddenFields,
}: {
  item: MigratedAuditLog;
  fieldLabels: Record<string, string>;
  hiddenFields: Set<string>;
}) {
  const fieldLabel = (fieldName: string) => fieldLabels[fieldName] || fieldName;
  const diffEntries = Object.entries(item.diff || {}).filter(
    ([field]) => !hiddenFields.has(field),
  );
  const snapshot = item.action === 'DELETE' ? item.dataBefore : item.dataAfter;
  const snapshotEntries = Object.entries(snapshot || {}).filter(
    ([field]) => !hiddenFields.has(field),
  );

  return (
    <div className="rounded-lg border border-border">
      <div className="flex flex-wrap items-center gap-2 border-b border-border bg-muted px-3 py-2">
        <Badge variant="outline" className={auditActionClass(item.action)}>
          {auditActionLabel(item.action)}
        </Badge>
        <span className="text-xs text-muted-foreground">
          {formatMigratedDateTime(item.createdAt)}
        </span>
        {item.userName && (
          <span className="text-xs text-muted-foreground">- {item.userName}</span>
        )}
      </div>

      <div className="space-y-2 px-3 py-2">
        {item.action === 'UPDATE' && diffEntries.length > 0 && (
          <div className="space-y-2">
            {diffEntries.map(([field, change]) => (
              <div key={field} className="text-xs">
                <div className="font-medium text-foreground">{fieldLabel(field)}</div>
                <div className="mt-1 space-y-1 text-muted-foreground">
                  <div className="whitespace-pre-wrap break-words">
                    Было: {formatAuditValue(change.old, field)}
                  </div>
                  <div className="whitespace-pre-wrap break-words">
                    Стало: {formatAuditValue(change.new, field)}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {item.action !== 'UPDATE' && snapshotEntries.length > 0 && (
          <div className="space-y-1 text-xs">
            {snapshotEntries.slice(0, 8).map(([field, value]) => (
              <div key={field} className="grid grid-cols-[96px_1fr] gap-2">
                <span className="text-muted-foreground">{fieldLabel(field)}:</span>
                <span className="whitespace-pre-wrap break-words">
                  {formatAuditValue(value, field)}
                </span>
              </div>
            ))}
          </div>
        )}

        {item.action === 'UPDATE' && diffEntries.length === 0 && (
          <p className="text-xs text-muted-foreground">Изменения без diff</p>
        )}
      </div>
    </div>
  );
}

export function MigratedCrudHistory({
  legacyLog,
  auditItems,
  total,
  isLoading,
  error,
  fieldLabels = {},
  hiddenFields,
  title = 'Журнал изменений',
  renderAuditEntry,
}: {
  legacyLog?: string | null;
  auditItems: MigratedAuditLog[];
  total?: number;
  isLoading: boolean;
  error: unknown;
  fieldLabels?: Record<string, string>;
  hiddenFields?: string[];
  title?: string;
  renderAuditEntry?: (item: MigratedAuditLog) => ReactNode | null;
}) {
  const hasError = Boolean(error);
  const legacyItems = useMemo(() => parseLegacyLogEntries(legacyLog), [legacyLog]);
  const timelineItems = useMemo(() => {
    const legacy = legacyItems.map((item, index) => ({
      type: 'legacy' as const,
      id: item.id,
      sortTime: item.sortTime ?? 0,
      fallbackOrder: index,
      item,
    }));
    const audit = auditItems.map((item, index) => ({
      type: 'audit' as const,
      id: item.id,
      sortTime: auditSortTime(item),
      fallbackOrder: legacy.length + index,
      item,
    }));

    return [...legacy, ...audit].sort((a, b) => {
      if (a.sortTime !== b.sortTime) return b.sortTime - a.sortTime;
      return a.fallbackOrder - b.fallbackOrder;
    });
  }, [auditItems, legacyItems]);
  const hiddenFieldSet = useMemo(
    () => new Set([...DEFAULT_HIDDEN_AUDIT_FIELDS, ...(hiddenFields || [])]),
    [hiddenFields],
  );
  const count = legacyItems.length + (total ?? auditItems.length);

  return (
    <section className="space-y-2">
      <h4 className="flex items-center gap-2 text-sm font-medium">
        <History className="h-4 w-4" />
        {title}
        <Badge variant="outline">{count}</Badge>
      </h4>

      {isLoading && (
        <div className="flex items-center gap-2 py-3 text-sm text-muted-foreground">
          <Loader2 className="h-4 w-4 animate-spin" />
          Загрузка журнала
        </div>
      )}

      {hasError && (
        <div className="flex items-center gap-2 py-3 text-sm text-destructive">
          <AlertCircle className="h-4 w-4" />
          Ошибка загрузки журнала
        </div>
      )}

      {!isLoading && !hasError && timelineItems.length === 0 && (
        <p className="text-sm text-muted-foreground">Записей нет</p>
      )}

      {!isLoading && !hasError && timelineItems.length > 0 && (
        <div className="space-y-2">
          {timelineItems.map((timelineItem) => {
            if (timelineItem.type === 'legacy') {
              return (
                <div key={timelineItem.id} className="overflow-hidden rounded-lg border border-border">
                  <div className="bg-muted px-3 py-2 text-xs font-medium">
                    {timelineItem.item.header}
                  </div>
                  <div className={`whitespace-pre-wrap break-words px-3 py-2 text-xs ${
                    timelineItem.item.isEmpty ? 'text-muted-foreground italic' : 'text-muted-foreground'
                  }`}>
                    <LinkText>{timelineItem.item.content}</LinkText>
                  </div>
                </div>
              );
            }

            const custom = renderAuditEntry?.(timelineItem.item);
            if (custom) return <div key={timelineItem.id}>{custom}</div>;

            return (
              <AuditEntry
                key={timelineItem.id}
                item={timelineItem.item}
                fieldLabels={fieldLabels}
                hiddenFields={hiddenFieldSet}
              />
            );
          })}
        </div>
      )}
    </section>
  );
}
